/* @flow */
import moment from 'moment';
import { prop } from 'ramda';
import {
  OrderedMap,
  Map,
  Record,
  List,
  Set,
  fromJS,
  type KeyedSeq,
  type IndexedSeq,
} from 'immutable';
import { toLower } from 'trustar/utils';
import isString from 'lodash.isstring';
import createDebugger from 'debug';

const debug = createDebugger('web_modules:graph-utils'); // eslint-disable-line no-unused-vars

/* Types
 * ======================================================================= */
export type EdgeShape = Record<RawEdgeShape>;

export type NodeShape = Record<RawNodeShape>;

export type GraphDataShape = Record<{
  edges: OrderedMap<string, EdgeShape>,
  nodes: OrderedMap<string, NodeShape>,
}>;

type FilterCategoryType =
    'bitcoin_address'
  | 'cidr_block'
  | 'cve'
  | 'email_address'
  | 'ip'
  | 'md5'
  | 'malware'
  | 'registry_key'
  | 'sha1'
  | 'sha256'
  | 'software'
  | 'url'
  | 'phone_number'
  | 'x_id'
  | 'threat_actor';

type FilterCategory = {
  type: FilterCategoryType,
  label: string,
};

type GraphFiltersShape = Record<{
  filterText: string,
  inactiveFilters: OrderedMap<FilterCategoryType, FilterCategory>,
}>;

type CurrentGraphShape = Record<{
  id: string,
  viewType: 'analysis' | 'report',
  type: 'RECORD' | 'INDICATOR',
  data: GraphDataShape,
  deletedNodes: Set<string>,
  filters: GraphFiltersShape,
  timeRange: [number, number],
  initialSettledState: typeof Graph, // TODO: Remove this
}>;

type GraphHistoryShape = Record<{
  index: number,
  timeline: List<CurrentGraphShape>,
}>;

type ForcePositionShape = Record<{
  id: string, // Holding on to the node id
  x: number,
  y: number,
  vx: ?number,
  vy: ?number,
  fx: ?number,
  fy: ?number,
  index: ?number,
}>;

type GraphMetaShape = Record<{
  id: ?string,
  nodePositions: Map<string, ForcePositionShape>,
  initialSettledState: ?GraphShape,
}>;

type GraphShape = Record<{
  currentGraph: CurrentGraphShape,
  history: GraphHistoryShape,
  meta: GraphMetaShape,
}>;

/* =======================================================================
 * End Types */

/* This is the single source of truth for our graph's node categories:
   NOTE: we should consolidate type and system at some point to keep this flat: */
export const filterCategories: OrderedMap<*, FilterCategory> = OrderedMap({
  'bitcoin_address': { type: 'bitcoin_address', label: 'Bitcoin Address' },
  'cidr_block': { type: 'cidr_block', label: 'CIDR Block' },
  'cve': { type: 'cve', label: 'CVE' },
  'email_address': { type: 'email_address', label: 'Email Address' },
  'ip': { type: 'ip', label: 'IP' },
  'md5': { type: 'md5', label: 'MD5 Hash' },
  'malware': { type: 'malware', label: 'Malware' },
  'registry_key': { type: 'registry_key', label: 'Registry Key' },
  'sha1': { type: 'sha1', label: 'SHA1 Hash' },
  'sha256': { type: 'sha256', label: 'SHA256 Hash' },
  'software': { type: 'software', label: 'Software' },
  'url': { type: 'url', label: 'URL' },
  'phone_number': { type: 'phone_number', label: 'Phone Number'},
  'x_id': { type: 'x_id', label: 'X-ID'},
  'threat_actor': { type: 'threat_actor', label: 'Threat Actor'}
});

export const Filters: GraphFiltersShape = Record({
  filterText: '',
  inactiveFilters: OrderedMap(),
});

export const getSectorAbrev = (sector: ?string): string => {
  if (!sector || !isString(sector)) {
    // Is this really an issue? This will just stringify whatever
    // we give it and not through an error as far as I know
    // $FlowIssue
    console.warn(`Invalid argument passed to getSectorAbrev: ${sector}`);
    return '?';
  }

  switch (toLower(sector)) {
  case 'information-technology':
  case 'information technology':
    return 'IT';
  case 'aviation':
    return 'AV';
  case 'energy':
    return 'ENG';
  case 'financial-services':
    return 'FS';
  case 'communications':
    return 'COM';
  case 'education':
    return 'EDU';
  case 'manufacturing':
    return 'MFT';
  case 'e-commerce':
  case 'commercial':
    return 'CO';
  case 'entertainment':
    return 'ENT';
  case 'insurance':
    return 'INS';
  case 'food-agriculture':
    return 'AGR';
  case 'automotive':
    return 'AUTO';
  case 'defense':
    return 'DEF';
  case 'government':
    return 'GOV';
  case 'health':
    return 'MED';
  case 'infrastructure':
    return 'INF';
  case 'nuclear':
    return 'NUC';
  case 'retail':
    return 'RTL';
  case 'sports':
    return 'SP';
  case 'transportation':
    return 'TRAN';
  default:
    console.warn(`Could not find abreviation for sector ${toLower(sector)}`);
    return '?';
  }
};

export const GraphData: GraphDataShape = Record({
  edges: OrderedMap(),
  nodes: OrderedMap(),
});

/**
 * NOTE: Immutable has no good concept of a tuple, but flow lets us use arrays
 * as tuples by enforcing their shape and length. That's why we use a mutable
 * array here.
 */

export const CurrentGraph: CurrentGraphShape = Record({
  id: '', // The id of the parent record, currently this means either a report or an analysis
  type: '',
  viewType: '', // The type of graph this is, either 'report' or 'analysis' currently
  indicatorType: null,
  value: null,
  data: GraphData(),
  deletedNodes: Set(), // Ids of deleted nodes
  filters: Filters(),
  timeRange: [0, 0],  // Selected time range to view. See NOTE on array usage
  initialSettledState: null, // TODO: Remove this and store in graph meta
});

/**
 * NOTE: ForcePositions sometimes become the node in a node list. This means
 * that in the getters we will try to set hasAnnotations on this record.
 * Specifically the "withPositions" getter. Therefore I've added the key here
 * and defaulted it to false.
 */
export const ForcePosition: ForcePositionShape = Record({
  id: null,
  x: 0,
  y: 0,
  vx: null,
  vy: null,
  fx: null,
  fy: null,
  index: null,
  hasAnnotations: false, // See NOTE
});

/**
 * NOTE: Not sure if the ID filtering is necessary but it was left over from
 * some older logic so I've kept it in.
 */
export const toNodePositions = ({ nodes, edges = [] }: RawGraph): Map<string, ForcePositionShape> => {
  return List(nodes)
    .filter(prop('id')) // Filter out anything without an ID. See NOTE
    .map(x => ForcePosition(x))
    .toMap()
    .mapKeys((k, v) => v.get('id'));
};

export const GraphMeta: GraphMetaShape = Record({
  id: null,
  nodePositions: Map(),
  initialSettledState: null,
});

export const GraphHistory: GraphHistoryShape = Record({
  index: 0,
  timeline: List(),
});

export const Graph: GraphShape = Record({
  currentGraph: CurrentGraph(),
  history: GraphHistory(), // Undo/redo history
  meta: GraphMeta(),
});

export const GraphOptions = Record({
  showLabels: false,
  showHelp: false,
  currentPanel: 'details', // Currently either: details | notes
});

/**
 * NOTE: We don't yet know why there would be anything without an id but it was
 * happening and causing unexpected behavior. So as a safety measure for now
 * we're going to filter those out.
 */
export const listToOrderedMap = <T>(list: Array<T>): OrderedMap<string, Record<T>> => {
  return fromJS(list)
    .filter(x => x.get('id')) // Filter out anything without an ID. See NOTE
    .toOrderedMap()
    .mapKeys((k, v) => v.get('id'));
};

export type RawGraph = {
  nodes: Object[], // TODO: type this. Should be RawNodeShape[]. But that currently causes a number of errors
  edges: RawEdgeShape[],
};

/**
 * NOTE: Edges are optional since sometimes we want to call this on only a list
 * of nodes.
 */
export const toGraphData = ({ nodes, edges = [] }: RawGraph): GraphDataShape => {
  return GraphData({
    nodes: listToOrderedMap(nodes),
    edges: listToOrderedMap(edges),
  });
};

/**
 * Filter a map of edges such that none of the edges are connected to any of the
 * node ids represented in the ids Set. This is useful when removing a node from
 * the graph, as we naturally want to remove all connected edges as well.
 *
 * Returns a predicate function that can be passed to a filter function.
 */
type NotConnectedToNodes = (ids: Set<string>) => Predicate<Map<string, *>>;
const notConnectedToNodes: NotConnectedToNodes = (ids) => (edge) => {
  const source = edge.get('source', '');
  const target = edge.get('target', '');

  return !ids.has(source) && !ids.has(target);
};

type ConnectedByEdges = (edges: OrderedMap<string, Map<string, *>>) => Predicate<Map<string, *>>;
export const connectedByEdges: ConnectedByEdges = (edges) => (x) => {
  return edges.some(edge => {
    const nodeId = x.get('id');
    return edge.get('source') === nodeId || edge.get('target') === nodeId;
  });
};

/**
 * Produces a predicate to determine whether or not an edge has both its source
 * and target nodes in a given list of nodes.
 */
const connectedToNodes = (nodes: Map<string, *>) => (edge: EdgeShape): boolean => {
  const source = edge.get('source', '');
  const target = edge.get('target', '');
  return nodes.get(source) && nodes.get(target);
};

/**
 * Takes a graph and a set of inactive filters and returns a Set of node Ids
 * of the nodes from that graph which would be filtered out.
 */
export const calculateFiltered = (graph: CurrentGraphShape, inactiveFiltersMap: Map<string, any>): Set<string> => {
  const inactiveFilters = inactiveFiltersMap.keySeq();
  return graph.getIn(['data', 'nodes']).filter((v, _) => {
    const enclaveGuids = v.get('enclaveGuids', []);
    const enclavesIntersect = inactiveFilters
      .filter(f => enclaveGuids.includes(f))
      .first();
    const hasType = inactiveFilters.includes(toLower(v.get('type')));
    const hasSystem = inactiveFilters.includes(toLower(v.get('system')));
    const hasTag = v.get('type') === 'INDICATOR_TAG' && inactiveFilters.includes(v.get('id'));
    return hasType || hasSystem || enclavesIntersect || hasTag;
  }).map((v, k) => v.get('id')).toSet();
};

const isSingleNodeGraph = (graph: CurrentGraphShape): boolean => {
  return (
    !graph.getIn(['data', 'edges']).size &&
    graph.getIn(['data', 'nodes']).size === 1
  );
};

/**
 * Remove the specified nodes from the graph. Also remove any associated edges
 * and any nodes which would become orphaned if the nodes specified in nodeIds
 * were deleted.
 */
export const removeNodes = (graph: CurrentGraphShape, nodeIds: Set<string>): CurrentGraphShape => {
  /*
   * NOTE: This defaults a graph with a single node and no edges to render, e.g. in the
   * case of an IOC with no correlations.
   *
   * We can revisit/refactor. This logic may be better elsewhere, also we need to
   * be sure this doesn't interfere with any graph when we are filtering or updating
   * the time range.
   *
   */
  if (isSingleNodeGraph(graph)) {
    return graph;
  }

  // remove nodes:
  const edges = graph.getIn(['data', 'edges'])
    .filter(notConnectedToNodes(nodeIds));
  const nodes = graph.getIn(['data', 'nodes'])
    .filter(x => !nodeIds.has(x.get('id'))) // Remove deleted nodes
    .filter(connectedByEdges(edges));       // Remove orphaned nodes

  return graph.set('data', toGraphData({ nodes, edges }));
};

export const filterNodesByTimeRange = (graph: CurrentGraphShape, timeRange: [ number, number ]): CurrentGraphShape => {
  const [ start, end ] = timeRange;

  debug('Filtering time range', timeRange);

  // Must have valid timestamps to filter against
  if (!start || !end) return graph;

  const nodes = graph.getIn(['data', 'nodes'])
    .filter(x => {
      // Not every node has a timestamp. TODO: Let's rethink this. What do we
      // want to do if a node does not have a timestamp, such as an indicator
      // node? We definitely still want to include it in the graph, but then
      // what if it becomes orphaned? In that case we probably want to remove it
      // as well. So we might need to do the connected by edges check after we
      // have done all this filtering
      const time = x.get('timeBegan') || x.get('time');
      if (!time) return true;

      const created = moment(time);
      return created.isBetween(start, end, null, '[]');
    });

  const edges = graph.getIn(['data', 'edges'])
    .filter(connectedToNodes(nodes));

  return graph.set('data', toGraphData({ nodes, edges }));

};

export const getNodeTime = (node: Map<string, any>) => node.get('timeBegan') || node.get('time');

type Iterable =
    Map<string, *>
  | List<*>
  | Set<*>
  | OrderedMap<string, *>
  | IndexedSeq<*>
  | KeyedSeq<string, *>;

export const getNodeTimes = (nodes: Iterable): number[] =>
  nodes
    .map(getNodeTime)
    .filter(Boolean) // Some nodes don't have timestamps. Filter them out
    .toIndexedSeq()
    .toArray();



// WEBPACK FOOTER //
// ./web_modules/trustar/constellation/graph-utils.js